<template>
  <div>
    <header>Menu</header>
    <div id="embed-div" v-if="store.pdfMenu"></div>
    <ListView
      v-else
      :list="store.recipesMap"
      :keyGetter="(item) => item.name"
      :itemTemplate="menuTemplate"
      :clickHandler="clickHandler"
    ></ListView>

    <Teleport to="body">
      <Flyout
        class="splash"
        :show="showSplash"
        @close="
          splashItem = null;
          showLabel = false;
        "
        :contentStyles="contentStyles"
      >
        <img class="splash-image" :src="imgSrc" alt="" />
        <div
          class="label_c"
          :ref="el => { if (el) labelContainerElement = el as HTMLDivElement }"
          :class="{ showing: showLabel }"
        >
          <component
            class="label"
            :is="labelTemplate"
            :data="labelData"
            :style="{ transform: 'scale(' + labelScale + ')' }"
          >
          </component>
        </div>
        <div class="splash-name">
          {{ splashItem?.name }}
          <button type="button" @click="showLabel = !showLabel" v-if="store.publishNutrition">
            Nutrition Info
          </button>
        </div>
      </Flyout>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
const runtimeConfig = useRuntimeConfig();
const adobeEmbedApiKey = runtimeConfig.public.adobeEmbedApiKey;
definePageMeta({
  title: "Menu",
  layout: "basic",
});

const store = useStore();
let tag = useCookie("tag").value;
store.setStore(tag);

useHead({
  title: `${tag} | Menu`,
});

const labelContainerElement = ref<HTMLDivElement>();
// const labelElement = ref<HTMLDivElement>();

const splashItem = ref<any>(null);
const showSplash = computed(() => (splashItem.value ? true : false));
const showLabel = ref(false);
const labelScale = ref(1);
const frame = ref<HTMLElement | null>(null);
const frameHeight = ref("600px");

const previewConfig = {
  embedMode: "IN_LINE",
}

onMounted(() => {
  if (store.pdfMenu) {
    if (!window.AdobeDC) {
      const script = document.createElement("script");
      script.src = "https://documentcloud.adobe.com/view-sdk/main.js";
      script.type = "text/javascript";
      script.async = true;
      document.body.appendChild(script);
      document.addEventListener("adobe_dc_view_sdk.ready", setAdobePreview);
    } else {
      setAdobePreview();
    }
  }
});

const imgSrc = computed(() => {
  if (splashItem.value?.image && store.tag)
    return getThumbsLink(splashItem.value.image, store.tag);
  else return "/placeholder.webp";
});

const labelData = computed(() => {
  if (splashItem.value) {
    switch (store.labelFormat) {
      case "au-std":
        return splashItem.value.labelAUStandardData;
      case "au-ext":
        return splashItem.value.labelAUExtendedData;
      case "us-std":
      default:
        return splashItem.value.labelUSStandardData;
      case "us-sim":
        return splashItem.value.labelUSSimplifiedData;
      case "us-tab":
        return splashItem.value.labelUSTabularData;
      case "us-stab":
        return splashItem.value.labelUSSmallTabularData;
      case "eu-std":
        return splashItem.value.labelEUStandardData;
      case "eu-ext":
        return splashItem.value.labelEUExtendedData;
      case "ca-bi":
        return splashItem.value.labelCABilingualData;
      case "ca-bi-hor":
        return splashItem.value.labelCABilingualHorizontalData;
      case "ca-std-eng": //same data for both
      case "ca-std-fr":
        return splashItem.value.labelCAStandardData;
    }
  }
});

const contentStyles = computed(() => {
  return {
    position: "relative",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundColor: "transparent",
    padding: 0,
    overflow: "hidden",
  };
});

const menuTemplate = computed(() => {
  // switch (store.menuItemFormat) {
  //   case "withImage":
  //   default:
      return defineAsyncComponent(
        () => import("@/components/templates/defaultTemplate.vue")
      );
//  }
});

const labelTemplate = computed(() => {
  switch (store.labelFormat) {
    case "au-std":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelAUStandard.vue")
      );
    case "au-ext":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelAUExtended.vue")
      );
    case "us-std":
    default:
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelUSStandard.vue")
      );
    case "us-sim":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelUSSimplified.vue")
      );
    case "us-tab":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelUSTabular.vue")
      );
    case "us-stab":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelUSSmallTabular.vue")
      );
    case "eu-std":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelEUStandard.vue")
      );
    case "eu-ext":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelEUExtended.vue")
      );
    case "ca-bi":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelCABilingual.vue")
      );
    case "ca-bi-hor":
      return defineAsyncComponent(
        () =>
          import("@/components/templates/labels/LabelCABilingualHorizontal.vue")
      );
    case "ca-std-eng":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelCAStandardEnglish.vue")
      );
    case "ca-std-fr":
      return defineAsyncComponent(
        () => import("@/components/templates/labels/LabelCAStandardFrench.vue")
      );
  }
});

function clickHandler(item: any) {
  splashItem.value = item;
  labelScale.value = 1;
  setTimeout(refreshLabelScale, 100);
}

function refreshLabelScale() {
  if (labelContainerElement.value) {
    let labelElement = labelContainerElement.value
      .firstElementChild as HTMLDivElement;
    let containerWidth = labelContainerElement.value.clientWidth * 0.9;
    let containerHeight = labelContainerElement.value.clientHeight * 0.9;
    let labelWidth = labelElement.clientWidth;
    let labelHeight = labelElement.clientHeight;

    let overWidth = labelWidth - containerWidth;
    let overHeight = labelHeight - containerHeight;
    if (overWidth > 0 || overHeight > 0) {
      if (overWidth > overHeight) {
        labelScale.value = containerWidth / labelWidth;
      } else {
        labelScale.value = containerHeight / labelHeight;
      }
    }
  }
}

function setAdobePreview() {
  var adobeDCView = new window.AdobeDC.View({
    clientId: adobeEmbedApiKey,
    divId: "embed-div",
  });
  adobeDCView.previewFile({
    content: {
      location: {
        url: store.pdfMenu,
      },
    },
    metaData: { fileName: store.pdfMenu },
  }, previewConfig);
}

// function resizeIframe() {
//   if (frame.value) {
//     frame.value.style.height = frame.value.contentWindow?.document.body.scrollHeight + 'px';
//   }
// }
</script>

<style scoped>
header {
  font-size: 4em;
  font-weight: 100;
  color: #505050;
  text-align: center;
  padding: 10px;
}

.pdf {
  width: 800px;
  margin: 0 auto;
}

.frame-object {
  width: 100%;
  height: 800px;
}
.splash-image {
  max-width: calc(100vw - 80px);
  max-height: calc(100vh - 80px);
}

.splash-name {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #505050;
  font-size: large;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(7px);
  margin-top: -43px;
  padding: 0px 20px;
}

.splash-name button {
  font-size: x-small;
  color: #505050;
  border: none;
  border-radius: 3px;
  background-color: rgba(255, 255, 255, 0.3);
  padding: 3px;
}
.splash-name button:hover {
  background-color: rgba(255, 255, 255, 0.6);
}

.label_c {
  position: absolute;
  height: calc(100% - 76px);
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.6);
  padding: 18px;
  transform: translateY(150%);
  transition: transform 0.5s ease-in-out;
}

.label_c.showing {
  transform: translateY(0);
}
</style>
```
